import { Linking, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useOpenWebPage } from '~/hooks/useOpenWebPage';
import { useToastMessages } from '~/hooks/useToastMessages';

/**
 * React hook that returns a function to open a maps application with a search query.
 * Handles platform differences automatically:
 * - Web: Opens Google Maps in the in-app browser
 * - iOS: Opens Apple Maps, falling back to Google Maps
 * - Android: Opens Google Maps via URL
 *
 * @returns A function that takes a location query string
 */
export function useOpenMaps() {
  const { t } = useTranslation();
  const openWebPage = useOpenWebPage();
  const { showErrorToast } = useToastMessages();

  return async (query: string) => {
    if (!query) return;

    try {
      const locationQuery = encodeURIComponent(query);

      if (Platform.OS === 'web') {
        openWebPage(`https://maps.google.com/maps?q=${locationQuery}`);
      } else {
        const mapsUrl = `maps://maps.apple.com/?q=${locationQuery}`;
        const canOpen = await Linking.canOpenURL(mapsUrl);

        if (canOpen) {
          await Linking.openURL(mapsUrl);
        } else {
          await Linking.openURL(`https://maps.google.com/maps?q=${locationQuery}`);
        }
      }
    } catch {
      showErrorToast(t('common.error.failedToOpenMaps', 'Failed to open maps'));
    }
  };
}
